import React, { useState } from "react";
import {
  TextField,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Checkbox,
  Divider,
  InputAdornment
} from "@mui/material";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DropzonePreview from "./utils/DropzonePreview";
import {
  cardContainerSx,
  headerSx,
  dragHandleSx,
  dragIconSx,
  questionTextSx,
  chipSx,
  collapseButtonSx,
  collapseIconSx,
  actionsButtonSx,
  actionsIconSx,
  bodySx,
  inputSx,
  previewAnswerSx,
  dividerSx,
  footerSx,
  footerInnerSx,
  checkboxSx,
  footerTextSx,
  menuPaperSx
} from "../survey/style/questionCardStyles";
import {
  BASE_QUESTION_CARD_DEFAULTS,
  BASE_QUESTION_CARD_MENU_LABELS,
  BASE_QUESTION_CARD_TEXTS,
  BASE_QUESTION_CARD_STYLES
} from "../survey/constants/constantsSurveys";

export default function BaseQuestionCard({
  question,
  index,
  showValidation,
  dragHandleProps,
  onDelete,
  onDuplicate,
  onUpdate,
  chipLabel,
  answerPlaceholder,
  answerFieldProps = BASE_QUESTION_CARD_DEFAULTS.answerFieldProps,
  answerEndAdornment = BASE_QUESTION_CARD_DEFAULTS.answerEndAdornment,
  extraContent = BASE_QUESTION_CARD_DEFAULTS.extraContent,
  canCollapse = BASE_QUESTION_CARD_DEFAULTS.canCollapse,
  showFooter = BASE_QUESTION_CARD_DEFAULTS.showFooter,
  footerContent = BASE_QUESTION_CARD_DEFAULTS.footerContent,
  menuItems = BASE_QUESTION_CARD_DEFAULTS.menuItems,
  headerSxOverride = BASE_QUESTION_CARD_DEFAULTS.headerSxOverride,
  extraHeaderContent = BASE_QUESTION_CARD_DEFAULTS.extraHeaderContent,
  chipSxOverride = BASE_QUESTION_CARD_DEFAULTS.chipSxOverride,
  cardSxOverride = BASE_QUESTION_CARD_DEFAULTS.cardSxOverride,
  questionTextSxOverride = BASE_QUESTION_CARD_DEFAULTS.questionTextSxOverride,
  questionLabel = BASE_QUESTION_CARD_DEFAULTS.questionLabel,
  isNested = BASE_QUESTION_CARD_DEFAULTS.isNested,
  parentOptionLabel = BASE_QUESTION_CARD_DEFAULTS.parentOptionLabel,
  parentOptionLabelColor = BASE_QUESTION_CARD_DEFAULTS.parentOptionLabelColor,
  showRequiredImagePreview =
  BASE_QUESTION_CARD_DEFAULTS.showRequiredImagePreview
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const open = Boolean(anchorEl);
  const shouldShowParentOptionLabel = Boolean(parentOptionLabel?.trim());

  const defaultMenuItems = isNested
    ? [
      {
        label: BASE_QUESTION_CARD_MENU_LABELS.delete,
        icon: <DeleteOutlineIcon sx={{ mr: 1 }} fontSize="small" />,
        onClick: onDelete
      }
    ]
    : [
      {
        label: BASE_QUESTION_CARD_MENU_LABELS.duplicate,
        icon: <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />,
        onClick: onDuplicate
      },
      {
        label: BASE_QUESTION_CARD_MENU_LABELS.delete,
        icon: <DeleteOutlineIcon sx={{ mr: 1 }} fontSize="small" />,
        onClick: onDelete
      }
    ];

  const resolvedMenuItems = menuItems || defaultMenuItems;

  const titleHasError = Boolean(
    question.shouldValidate &&
    (!question.title || question.title.trim() === "")
  );

  const resolvedQuestionLabel =
    questionLabel || `${BASE_QUESTION_CARD_TEXTS.questionPrefix} ${index + 1}:`;

  const truncatedParentOptionLabel =
    `${parentOptionLabel.slice(0, BASE_QUESTION_CARD_STYLES.parentOptionPreviewLength)}${parentOptionLabel.length >
      BASE_QUESTION_CARD_STYLES.parentOptionEllipsisThreshold
      ? BASE_QUESTION_CARD_TEXTS.ellipsis
      : ""
    }`;

  return (
    <Box
      sx={{
        ...cardContainerSx,
        ...(cardSxOverride || {})
      }}
    >
      <Box
        sx={{
          ...headerSx(collapsed),
          ...(headerSxOverride || {}),
          ...BASE_QUESTION_CARD_STYLES.headerBase,
          gap: isNested
            ? BASE_QUESTION_CARD_STYLES.headerNestedGap
            : BASE_QUESTION_CARD_STYLES.headerDefaultGap,
          px: isNested ? BASE_QUESTION_CARD_STYLES.headerNestedPx : undefined,
          py: isNested ? BASE_QUESTION_CARD_STYLES.headerNestedPy : undefined
        }}
      >
        <Box
          {...dragHandleProps}
          sx={{
            ...dragHandleSx,
            mr: isNested ? BASE_QUESTION_CARD_STYLES.dragHandleNestedMr : undefined,
            flexShrink: 0
          }}
        >
          <DragIndicatorIcon
            sx={{
              ...dragIconSx,
              fontSize: isNested
                ? BASE_QUESTION_CARD_STYLES.dragIconNestedFontSize
                : dragIconSx?.fontSize
            }}
          />
        </Box>

        <Typography
          sx={{
            ...questionTextSx,
            ...(questionTextSxOverride || {}),
            flexShrink: 1,
            minWidth: 0,
            fontSize: isNested
              ? BASE_QUESTION_CARD_STYLES.nestedQuestionTextFontSize
              : undefined
          }}
        >
          {resolvedQuestionLabel}
        </Typography>

        {shouldShowParentOptionLabel && (
          <Typography
            sx={{
              ml: isNested
                ? BASE_QUESTION_CARD_STYLES.parentOptionNestedMl
                : BASE_QUESTION_CARD_STYLES.parentOptionDefaultMl,
              fontSize: isNested
                ? BASE_QUESTION_CARD_STYLES.parentOptionNestedFontSize
                : BASE_QUESTION_CARD_STYLES.parentOptionDefaultFontSize,
              color: parentOptionLabelColor,
              fontWeight: 500,
              fontStyle: "italic",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mr: 1,
              maxWidth: isNested
                ? BASE_QUESTION_CARD_STYLES.parentOptionNestedMaxWidth
                : BASE_QUESTION_CARD_STYLES.parentOptionDefaultMaxWidth
            }}
          >
            {BASE_QUESTION_CARD_TEXTS.parentOptionPrefix}
            {truncatedParentOptionLabel}
            {BASE_QUESTION_CARD_TEXTS.quoteSuffix}
          </Typography>
        )}

        <Chip
          label={chipLabel}
          size="small"
          sx={{
            ...chipSx,
            ...(chipSxOverride || {}),
            flexShrink: 1,
            minWidth: 0,
            maxWidth: isNested
              ? BASE_QUESTION_CARD_STYLES.nestedChipMaxWidth
              : BASE_QUESTION_CARD_STYLES.defaultChipMaxWidth,
            "& .MuiChip-label": {
              px: isNested ? BASE_QUESTION_CARD_STYLES.nestedChipLabelPx : undefined,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: isNested
                ? BASE_QUESTION_CARD_STYLES.nestedChipLabelFontSize
                : undefined
            }
          }}
        />

        {extraHeaderContent && (
          <Box sx={BASE_QUESTION_CARD_STYLES.extraHeaderContent}>
            {extraHeaderContent}
          </Box>
        )}

        <Box sx={BASE_QUESTION_CARD_STYLES.flexGrowSpacer} />

        <Box
          sx={{
            ...BASE_QUESTION_CARD_STYLES.actionsContainer,
            gap: isNested
              ? BASE_QUESTION_CARD_STYLES.actionsNestedGap
              : BASE_QUESTION_CARD_STYLES.actionsDefaultGap
          }}
        >
          {canCollapse && (
            <IconButton
              onClick={() => setCollapsed((prev) => !prev)}
              disableRipple
              sx={{
                ...collapseButtonSx,
                p: isNested
                  ? BASE_QUESTION_CARD_STYLES.iconButtonNestedPadding
                  : collapseButtonSx?.p
              }}
            >
              <KeyboardArrowUpIcon
                sx={{
                  ...collapseIconSx(collapsed),
                  fontSize: isNested
                    ? BASE_QUESTION_CARD_STYLES.actionIconNestedFontSize
                    : undefined
                }}
              />
            </IconButton>
          )}

          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disableRipple
            sx={{
              ...actionsButtonSx,
              p: isNested
                ? BASE_QUESTION_CARD_STYLES.iconButtonNestedPadding
                : actionsButtonSx?.p
            }}
          >
            <MoreVertIcon
              sx={{
                ...actionsIconSx,
                fontSize: isNested
                  ? BASE_QUESTION_CARD_STYLES.actionIconNestedFontSize
                  : undefined
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Box sx={bodySx(canCollapse ? collapsed : false)}>
        <TextField
          fullWidth
          value={question.title || ""}
          onChange={(e) => onUpdate?.({ title: e.target.value })}
          placeholder={BASE_QUESTION_CARD_TEXTS.titlePlaceholder}
          size="small"
          error={titleHasError}
          helperText={
            titleHasError
              ? BASE_QUESTION_CARD_TEXTS.requiredTitle
              : undefined
          }
          sx={inputSx}
        />

        {(!canCollapse || !collapsed) && (
          <>
            <TextField
              fullWidth
              value={question.description || ""}
              onChange={(e) => onUpdate?.({ description: e.target.value })}
              placeholder={BASE_QUESTION_CARD_TEXTS.descriptionPlaceholder}
              size="small"
              sx={inputSx}
            />

            {extraContent ? (
              extraContent
            ) : (
              <TextField
                fullWidth
                placeholder={answerPlaceholder}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  tabIndex: -1,
                  ...(answerEndAdornment
                    ? {
                        endAdornment: (
                          <InputAdornment position="end" sx={{ pointerEvents: "none" }}>
                            {answerEndAdornment}
                          </InputAdornment>
                        )
                      }
                    : {})
                }}
                sx={previewAnswerSx}
                {...answerFieldProps}
              />
            )}

            {showRequiredImagePreview && question.requiredImage && (
              <DropzonePreview />
            )}
          </>
        )}
      </Box>

      {(!canCollapse || !collapsed) && showFooter && (
        <>
          <Divider sx={dividerSx} />

          <Box sx={footerSx}>
            {footerContent || (
              <Box sx={footerInnerSx}>
                <Checkbox
                  checked={Boolean(question.requiredImage)}
                  onChange={(e) =>
                    onUpdate?.({ requiredImage: e.target.checked })
                  }
                  disableRipple
                  sx={checkboxSx}
                />

                <Typography sx={footerTextSx}>
                  {BASE_QUESTION_CARD_TEXTS.requiredImageFooter}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: menuPaperSx
        }}
      >
        {resolvedMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              item.onClick?.();
              setAnchorEl(null);
            }}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
